import type { Ref } from 'react';
import { memo, forwardRef, useContext } from 'react';
import { ChevronRight } from 'react-feather';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { cx } from '@trinity/chat-components/utils';
import styles from './style.module.css';

export type PopperMenuProps = {
  children: React.ReactNode;
  className?: string;
};

export const PopperMenu = memo(
  forwardRef((props: PopperMenuProps, ref: Ref<HTMLDivElement>) => {
    return (
      <div className={cx(styles.menu, props.className)} ref={ref}>
        {props.children}
      </div>
    );
  })
);

export type PopperMenuItemProps = {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
};

export const PopperMenuItem = memo(
  forwardRef((props: PopperMenuItemProps, ref: Ref<HTMLDivElement>) => {
    const { palette, theme: wl } = useContext(AppThemingContext);

    const className = cx(styles.item, props.className, props.disabled ? styles.disabled : undefined);
    const onClick = props.disabled ? undefined : props.onClick;
    const onMouseDown = props.disabled ? undefined : props.onMouseDown;

    // Create whitelabel styles for hover state
    const whitelabelStyles = wl?.enabled
      ? ({
          '--menu-item-hover-color': palette.primary.dark,
          '--menu-item-hover-bg': palette.primary.lighter,
        } as React.CSSProperties)
      : {};

    return (
      <div
        ref={ref}
        className={className}
        onClick={onClick}
        onMouseDown={onMouseDown}
        style={whitelabelStyles}
      >
        {props.children}
      </div>
    );
  })
);

export const ParentPopperMenuItem = memo(
  forwardRef(({ children, ...props }: PopperMenuItemProps, ref: Ref<HTMLDivElement>) => {
    const className = cx(styles.parent, props.className);

    return (
      <PopperMenuItem {...props} className={className} ref={ref}>
        {children}
        <ChevronRight className={styles.chevron} />
      </PopperMenuItem>
    );
  })
);

export default PopperMenu;
